import dayjs from 'dayjs';
import { DATE_FORMATS, DATE_FORMAT_HYPHEN } from 'utilities/constants';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import Env from 'Env';

dayjs.extend(utc);
dayjs.extend(timezone);

// Evaluated lazily so the feature flag is read at call time, not frozen at module-load time.
const getEvProductLabel = () => (Env.getIsEvWay4Enabled() ? 'AmpCharge EV Charging' : 'EV Public Charging');

interface Filters {
  isNext130Days?: boolean;
  isNext3Months?: boolean;
  isNext6Months?: boolean;
  isNextMonth?: boolean;
  isPrior12Months?: boolean;
  isPrior6Months?: boolean;
  isPrior3Months?: boolean;
  isPriorMonth?: boolean;
  processedDateFrom?: string;
  processedDateTo?: string;
  costCentre?: string;
  domesticNumber?: string;
  cardNumber?: string;
  registration?: string;
  minNumberOfCarWashes?: number;
  minNumberOfFills?: number;
  minTransactionVolume?: number;
  minTransactionAmount?: number;
  locations?: string[];
  monthToDateCardLimit?: number;
  isCreditsOnly?: boolean;
  isFuelPay?: boolean;
  isActiveVehicles?: boolean;
  transaction10PM5AM?: boolean;
  products?: string[];
}

export interface ScheduledReport {
  reportName?: string;
  reportDescription?: string;
  filters?: Filters;
  outputFormat?: string;
  reportSchedule?: {
    scheduleFrequency?: string;
    reportingDayOfWeek?: string;
    scheduleReportingPeriod?: string;
  };
  email?: {
    to?: string;
    cc?: string;
    subject?: string;
    body?: string;
  };
  reportScheduleId?: string;
  reportTypeId?: string;
}

export interface FormValues {
  reportName?: string;
  reportDescription?: string;
  processedDateFrom?: Date | string | null;
  processedDateTo?: Date | string | null;
  costCentre?: string;
  domesticNumber?: string;
  cardNumber?: string;
  registration?: string;
  cardRenewalPeriod?: string;
  minNumberOfCarWashes?: string;
  minNumberOfFills?: string;
  minTransactionVolume?: string;
  minTransactionAmount?: string;
  locations?: string;
  monthToDateCardLimit?: string;
  isCreditsOnly?: boolean;
  isFuelPay?: boolean;
  isActiveVehicles?: boolean;
  transaction10PM5AM?: boolean;
  allProducts?: boolean;
  [key: string]: any;
  outputFormat?: string;
  createSchedule?: string;
  scheduleFrequency?: string;
  reportingDayOfWeek?: string;
  scheduleReportingPeriod?: string;
  emailTo?: string;
  emailCc?: string;
  emailSubject?: string;
  emailBody?: string;
}

const getCardRenewalPeriod = (filters: Filters): string | null => {
  if (filters?.isNext130Days) {
    return 'next130days';
  } else if (filters?.isNext3Months) {
    return 'next3mths';
  } else if (filters?.isNext6Months) {
    return 'next6mths';
  } else if (filters?.isNextMonth) {
    return 'nextMth';
  } else if (filters?.isPrior12Months) {
    return 'prior12mths';
  } else if (filters?.isPrior6Months) {
    return 'prior6mths';
  } else if (filters?.isPrior3Months) {
    return 'prior3mths';
  } else if (filters?.isPriorMonth) {
    return 'priorMth';
  }

  return null;
};

// Base products array - includes all products
const allProductsBase = [
  { label: 'PREMIUM 98 A', value: 'v98', code: 'V98' },
  { label: 'Ulp/E10/LAF91/EFlex', value: 'ulp', code: 'ULP' },
  { label: 'PREMIUM 95 A/Pulp', value: 'v95', code: 'V95' },
  { label: 'Diesel/PDSLA/AdBlue Pump', value: 'dis', code: 'DIS' },
  { label: 'LPG', value: 'lpg', code: 'LPG' },
  { label: getEvProductLabel(), value: 'ev', code: 'EV' }, // Feature flag: EV Way4
  { label: 'Oils/Lubricants/AdbluePk', value: 'oil', code: 'OIL' },
  { label: 'Car Wash', value: 'cwa', code: 'CWA' },
  { label: 'Goods', value: 'gds', code: 'GDS' },
  { label: 'Bottled Gas', value: 'bg', code: 'B/G' },
  { label: 'Workshop', value: 'wrk', code: 'WRK' },
  { label: 'Service Parts', value: 'srv', code: 'SRV' },
  { label: 'CNG', value: 'gng', code: 'GNG' },
];

// Evaluated lazily so the feature flag is read at call time, not frozen at module-load time.
export const getProducts = () =>
  Env.getIsEvWay4Enabled()
    ? allProductsBase.map((p) => (p.code === 'EV' ? { ...p, label: getEvProductLabel() } : p))
    : allProductsBase.filter((p) => p.code !== 'EV');

type ProductOption = ReturnType<typeof getProducts>[number];

/**
 * Checks if all products are selected based on their labels.
 * @param {Object} values - The object containing product selections.
 * @returns {boolean} True if all products are selected, false otherwise.
 */
const isAllProductsSelected = (values: string[]): boolean => {
  const allProducts = getProducts().map((product) => product.label);
  return allProducts.every((label) => values?.includes(label));
};

/**
 * Checks if all products are selected based on their values.
 * @param {Object} values - The object containing product selections.
 * @param {Array} availableProducts - Optional array of available products to check against. Defaults to all products.
 * @returns {boolean} True if all products are selected, false otherwise.
 */
export const isAllProductsSelectedByValues = (
  values: { [key: string]: boolean },
  availableProducts?: ProductOption[]
): boolean => {
  const productsToCheck = availableProducts || getProducts();
  const allProducts = productsToCheck.map((product) => product.value);
  return allProducts.every((value) => !!values[value]);
};

const getSelectedProductLabels = (values: { [key: string]: boolean }): string[] => {
  if (isAllProductsSelectedByValues(values)) return [];

  return getProducts()
    .filter((product) => values[product.value])
    .map((product) => product.label);
};

export function mapScheduledReportToFormValues(scheduledReport: ScheduledReport): FormValues {
  /**
   * Maps product selections from a scheduled report to an object with product values as keys and truthy selection status as values.
   *
   * @example
   * // {
   * //   v98: true,
   * //   ulp: true,
   * //   v95: false,
   * //   ...
   * // }
   */
  const productSelections = getProducts().reduce(
    (productSelections, product) => {
      productSelections[product.value] = scheduledReport?.filters?.products?.includes(product.label) ?? false;
      return productSelections;
    },
    {} as { [key: string]: boolean }
  );

  const formValues: FormValues = {
    reportName: scheduledReport?.reportName,
    reportDescription: scheduledReport?.reportDescription,

    processedDateFrom: scheduledReport?.filters?.processedDateFrom
      ? dayjs(scheduledReport?.filters?.processedDateFrom, DATE_FORMAT_HYPHEN).toDate()
      : null,
    processedDateTo: scheduledReport?.filters?.processedDateTo
      ? dayjs(scheduledReport?.filters?.processedDateTo, DATE_FORMAT_HYPHEN).toDate()
      : null,

    costCentre: scheduledReport?.filters?.costCentre,
    domesticNumber: scheduledReport?.filters?.domesticNumber,
    cardNumber: scheduledReport?.filters?.cardNumber,
    registration: scheduledReport?.filters?.registration,

    cardRenewalPeriod: getCardRenewalPeriod(scheduledReport?.filters ?? {}) ?? undefined,

    minNumberOfCarWashes: scheduledReport?.filters?.minNumberOfCarWashes?.toString(),
    minNumberOfFills: scheduledReport?.filters?.minNumberOfFills?.toString(),
    minTransactionVolume: scheduledReport?.filters?.minTransactionVolume?.toString(),
    minTransactionAmount: scheduledReport?.filters?.minTransactionAmount?.toString(),
    locations: scheduledReport?.filters?.locations ? scheduledReport?.filters?.locations?.join(', ') : undefined,

    isCreditsOnly: scheduledReport?.filters?.isCreditsOnly,
    isFuelPay: scheduledReport?.filters?.isFuelPay,
    isActiveVehicles: scheduledReport?.filters?.isActiveVehicles,
    transaction10PM5AM: scheduledReport?.filters?.transaction10PM5AM,

    allProducts: isAllProductsSelected(scheduledReport?.filters?.products || []),
    ...productSelections,

    outputFormat: scheduledReport?.outputFormat,

    createSchedule:
      scheduledReport?.reportSchedule?.scheduleFrequency &&
      scheduledReport?.reportSchedule?.scheduleFrequency !== 'Adhoc'
        ? 'yes'
        : 'no',
    scheduleFrequency: scheduledReport?.reportSchedule?.scheduleFrequency ?? 'Monthly',
    reportingDayOfWeek: scheduledReport?.reportSchedule?.reportingDayOfWeek || getCurrentDayOfWeek(),

    scheduleReportingPeriod: scheduledReport?.reportSchedule?.scheduleReportingPeriod ?? 'CalendarMonth',

    emailTo: scheduledReport?.email?.to,
    emailCc: scheduledReport?.email?.cc,
    emailSubject: scheduledReport?.email?.subject,
    emailBody: scheduledReport?.email?.body,
  };

  if (scheduledReport?.filters?.monthToDateCardLimit)
    formValues.monthToDateCardLimit = scheduledReport?.filters?.monthToDateCardLimit?.toString();

  return formValues;
}

export function mapFormValuesToRequest(
  scheduledReport: ScheduledReport | null,
  reportTypeId: string,
  values: FormValues
) {
  // Temporary fix for TASK 25758
  // Create card passes dates as date object, and edit card as date strings

  let dateFrom = '';
  let dateTo = '';

  if (values.processedDateFrom && values.processedDateTo) {
    const processedDateFrom = values.processedDateFrom;
    const processedDateTo = values.processedDateTo;

    if (processedDateFrom instanceof Date) {
      dateFrom = dayjs(processedDateFrom).format(DATE_FORMAT_HYPHEN);
      dateTo = dayjs(processedDateTo).format(DATE_FORMAT_HYPHEN);
    } else {
      dateFrom = dayjs(processedDateFrom, DATE_FORMATS).format(DATE_FORMAT_HYPHEN);
      dateTo = dayjs(processedDateTo, DATE_FORMATS).format(DATE_FORMAT_HYPHEN);
    }
  }

  const request = {
    scheduleId: scheduledReport ? scheduledReport.reportScheduleId : null,
    reportTypeId: reportTypeId,
    reportName: values.reportName,
    reportDescription: values.reportDescription,
    outputFormat: values.outputFormat,
    reportSchedule: {
      scheduleFrequency: values.createSchedule === 'yes' ? values.scheduleFrequency : null,
      reportingDayOfWeek:
        values.createSchedule === 'yes' && values?.scheduleFrequency === 'Weekly' ? values.reportingDayOfWeek : null,
      scheduleReportingPeriod: values.createSchedule === 'yes' ? values.scheduleReportingPeriod : null,
    },
    email: {
      to: values.emailTo,
      cc: values.emailCc,
      subject: values.emailSubject,
      body: values.emailBody,
    },
    filters: {
      processedDateFrom: values.processedDateFrom && values.createSchedule !== 'yes' ? dateFrom : null,
      processedDateTo: values.processedDateTo && values.createSchedule !== 'yes' ? dateTo : null,

      costCentre: values.costCentre?.toUpperCase(),
      domesticNumber: values.domesticNumber?.toUpperCase(),
      cardNumber: values.cardNumber,
      registration: values.registration?.toUpperCase(),

      isNext130Days: values.cardRenewalPeriod ? values.cardRenewalPeriod === 'next130days' : null,
      isNext3Months: values.cardRenewalPeriod ? values.cardRenewalPeriod === 'next3mths' : null,
      isNext6Months: values.cardRenewalPeriod ? values.cardRenewalPeriod === 'next6mths' : null,
      isNextMonth: values.cardRenewalPeriod ? values.cardRenewalPeriod === 'nextMth' : null,
      isPrior12Months: values.cardRenewalPeriod ? values.cardRenewalPeriod === 'prior12mths' : null,
      isPrior6Months: values.cardRenewalPeriod ? values.cardRenewalPeriod === 'prior6mths' : null,
      isPrior3Months: values.cardRenewalPeriod ? values.cardRenewalPeriod === 'prior3mths' : null,
      isPriorMonth: values.cardRenewalPeriod ? values.cardRenewalPeriod === 'priorMth' : null,

      minNumberOfCarWashes: values.minNumberOfCarWashes ? parseInt(values.minNumberOfCarWashes) : null,
      minNumberOfFills: values.minNumberOfFills ? parseInt(values.minNumberOfFills) : null,
      minTransactionVolume: values.minTransactionVolume ? parseInt(values.minTransactionVolume) : null,
      minTransactionAmount: values.minTransactionAmount ? parseInt(values.minTransactionAmount) : null,
      monthToDateCardLimit: values.monthToDateCardLimit ? values.monthToDateCardLimit : null,

      isCreditsOnly: values.isCreditsOnly,
      isFuelPay: values.isFuelPay,
      isActiveVehicles: values.isActiveVehicles,
      transaction10PM5AM: values.transaction10PM5AM,

      locations: values?.locations ? values.locations?.split(',').map((x) => x.trim()) : null,
      products: getSelectedProductLabels(values),
    },
  };

  return request;
}

export function getCurrentDayOfWeek(): string {
  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const dayIndex = dayjs().day();

  if (dayIndex === 0 || dayIndex === 6) {
    return 'Monday';
  } else {
    return daysOfWeek[dayIndex];
  }
}

/**
 * Condition to show Financial Year Radio Button
 * TASK 47258
 * @returns Boolean
 */
export const showFinancialYearRadioButton = (formConfig: {
  scheduleOptions?: { removeFytdUntilJuly?: boolean };
}): boolean => {
  const currentDate = dayjs().tz('Australia/Sydney');
  return formConfig?.scheduleOptions?.removeFytdUntilJuly ? currentDate.isAfter('2024-06-30', 'day') : true;
};
